import { Link } from "@tanstack/react-router";
import { ArrowUpRight, CircleAlert, CircleCheck } from "lucide-react";
import type React from "react";
import { useEffect, useId, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
	NativeSelect,
	NativeSelectOption,
} from "@/components/ui/native-select";
import { Textarea } from "@/components/ui/textarea";
import { ANALYTICS_EVENTS, useAnalytics } from "@/lib/analytics";
import { BOOKING_URL, CONTACT_EMAIL, CONTACT_URL } from "@/lib/constants";
import {
	BOOKABLE_NEEDS,
	type ContactField,
	type ContactNeed,
	contactFieldsSchema,
	submitContactRequest,
} from "@/lib/contact";
import { useI18n } from "@/lib/i18n/use-i18n";

type FormValues = Record<ContactField, string> & { nickname: string };
type ErrorKey = "required" | "email" | "situation" | "url";
type Status = "idle" | "submitting" | "success" | "failure";

/** Field order, used to focus the first invalid field. */
const FIELDS: ContactField[] = [
	"name",
	"email",
	"company",
	"need",
	"situation",
	"timeline",
	"budget",
	"website",
];

const FORMAT_ERRORS: Partial<Record<ContactField, ErrorKey>> = {
	email: "email",
	situation: "situation",
	website: "url",
};

const emptyValues = (need: ContactNeed | ""): FormValues => ({
	name: "",
	email: "",
	company: "",
	need,
	situation: "",
	timeline: "",
	budget: "",
	website: "",
	nickname: "",
});

const validate = (values: FormValues) => {
	const result = contactFieldsSchema.safeParse(values);
	const errors: Partial<Record<ContactField, ErrorKey>> = {};
	if (result.success) return errors;
	for (const issue of result.error.issues) {
		const field = issue.path[0] as ContactField;
		if (errors[field]) continue;
		errors[field] =
			values[field].trim() === ""
				? "required"
				: (FORMAT_ERRORS[field] ?? "required");
	}
	return errors;
};

export function ContactForm({ initialNeed }: { initialNeed?: ContactNeed }) {
	const { dict, locale, localeParams } = useI18n();
	const { capture } = useAnalytics();
	const form = dict.contact.form;
	const uid = useId();
	const idOf = (name: string) => `${uid}-${name}`;
	const [values, setValues] = useState(() => emptyValues(initialNeed ?? ""));
	const [errors, setErrors] = useState<Partial<Record<ContactField, ErrorKey>>>(
		{},
	);
	const [status, setStatus] = useState<Status>("idle");
	const startedAt = useRef(0);
	const formRef = useRef<HTMLFormElement>(null);
	const statusRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		startedAt.current = Date.now();
	}, []);

	// Offer pages link here with a preselected need; keep it in sync.
	useEffect(() => {
		if (initialNeed)
			setValues((current) => ({ ...current, need: initialNeed }));
	}, [initialNeed]);

	useEffect(() => {
		if (status === "success" || status === "failure")
			statusRef.current?.focus();
	}, [status]);

	const update =
		(field: keyof FormValues) =>
		(
			event: React.ChangeEvent<
				HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
			>,
		) => {
			const { value } = event.target;
			setValues((current) => ({ ...current, [field]: value }));
			if (field !== "nickname" && errors[field]) {
				setErrors((current) => ({ ...current, [field]: undefined }));
			}
		};

	const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		const nextErrors = validate(values);
		setErrors(nextErrors);
		const firstInvalid = FIELDS.find((field) => nextErrors[field]);
		if (firstInvalid) {
			formRef.current
				?.querySelector<HTMLElement>(`[name="${firstInvalid}"]`)
				?.focus();
			return;
		}

		setStatus("submitting");
		const parsed = contactFieldsSchema.parse(values);
		let delivered = false;
		try {
			const result = await submitContactRequest({
				data: {
					...parsed,
					locale,
					nickname: values.nickname,
					elapsedMs: Date.now() - startedAt.current,
				},
			});
			delivered = result.ok;
		} catch {
			delivered = false;
		}
		setStatus(delivered ? "success" : "failure");
		capture(ANALYTICS_EVENTS.contactSubmit, {
			need: parsed.need,
			status: delivered ? "success" : "failure",
		});
	};

	if (status === "success") {
		const canBook = BOOKABLE_NEEDS.has(values.need as ContactNeed);
		return (
			<div
				ref={statusRef}
				tabIndex={-1}
				aria-live="polite"
				className="flex flex-col items-start gap-4 outline-none"
			>
				<CircleCheck aria-hidden="true" className="size-8" />
				<div className="space-y-1">
					<p className="text-xl font-semibold">{form.success.title}</p>
					<p className="text-muted-foreground">
						{canBook
							? form.success.booking.description
							: form.success.description}
					</p>
				</div>
				{canBook ? (
					<div className="space-y-3">
						<Button size="lg" asChild>
							<a
								href={BOOKING_URL}
								target="_blank"
								rel="noopener"
								onClick={() =>
									capture(ANALYTICS_EVENTS.ctaClick, {
										cta: "booking",
										placement: "contact_success",
										href: BOOKING_URL,
									})
								}
							>
								{form.success.booking.button}
								<ArrowUpRight aria-hidden="true" />
							</a>
						</Button>
						<p className="text-sm text-muted-foreground">
							{form.success.booking.fallback}
						</p>
					</div>
				) : null}
				<Button
					variant="outline"
					onClick={() => {
						setValues(emptyValues(values.need as ContactNeed));
						startedAt.current = Date.now();
						setStatus("idle");
					}}
				>
					{form.success.again}
				</Button>
			</div>
		);
	}

	const need = (values.need || "other") as ContactNeed;
	const fieldProps = (field: ContactField, hint?: boolean) => {
		const describedBy = [
			hint ? idOf(`${field}-hint`) : null,
			errors[field] ? idOf(`${field}-error`) : null,
		]
			.filter(Boolean)
			.join(" ");
		return {
			id: idOf(field),
			name: field,
			value: values[field],
			onChange: update(field),
			"aria-invalid": errors[field] ? true : undefined,
			"aria-describedby": describedBy || undefined,
		};
	};
	const fieldError = (field: ContactField) =>
		errors[field] ? (
			<p id={idOf(`${field}-error`)} className="text-sm text-destructive">
				{form.errors[errors[field]]}
			</p>
		) : null;
	const optionalMark = (
		<span className="font-normal text-muted-foreground">({form.optional})</span>
	);
	const hasErrors = Object.values(errors).some(Boolean);

	return (
		<form
			ref={formRef}
			noValidate
			onSubmit={handleSubmit}
			className="space-y-6"
			aria-describedby={idOf("required-hint")}
		>
			<p id={idOf("required-hint")} className="text-sm text-muted-foreground">
				{form.requiredHint}
			</p>

			<div className="grid gap-6 sm:grid-cols-2">
				<div className="space-y-2">
					<Label htmlFor={idOf("name")}>{form.name}</Label>
					<Input {...fieldProps("name")} autoComplete="name" required />
					{fieldError("name")}
				</div>
				<div className="space-y-2">
					<Label htmlFor={idOf("email")}>{form.email}</Label>
					<Input
						{...fieldProps("email")}
						type="email"
						autoComplete="email"
						required
					/>
					{fieldError("email")}
				</div>
				<div className="space-y-2">
					<Label htmlFor={idOf("company")}>{form.company}</Label>
					<Input
						{...fieldProps("company")}
						autoComplete="organization"
						required
					/>
					{fieldError("company")}
				</div>
				<div className="space-y-2 [&_[data-slot=native-select-wrapper]]:w-full">
					<Label htmlFor={idOf("need")}>{form.need}</Label>
					<NativeSelect {...fieldProps("need")} required>
						<NativeSelectOption value="" disabled>
							{form.needPlaceholder}
						</NativeSelectOption>
						{Object.entries(form.needOptions).map(([key, label]) => (
							<NativeSelectOption key={key} value={key}>
								{label}
							</NativeSelectOption>
						))}
					</NativeSelect>
					{fieldError("need")}
				</div>
			</div>

			<div className="space-y-2">
				<Label htmlFor={idOf("situation")}>{form.situation}</Label>
				<p
					id={idOf("situation-hint")}
					className="text-sm text-muted-foreground"
				>
					{form.situationHints[need]}
				</p>
				<Textarea
					{...fieldProps("situation", true)}
					rows={6}
					className="min-h-36"
					required
				/>
				{fieldError("situation")}
			</div>

			<div className="grid gap-6 sm:grid-cols-2">
				<div className="space-y-2">
					<Label htmlFor={idOf("timeline")}>
						{form.timeline} {optionalMark}
					</Label>
					<Input
						{...fieldProps("timeline")}
						placeholder={form.timelinePlaceholder}
					/>
					{fieldError("timeline")}
				</div>
				<div className="space-y-2">
					<Label htmlFor={idOf("budget")}>
						{form.budget} {optionalMark}
					</Label>
					<Input
						{...fieldProps("budget")}
						placeholder={form.budgetPlaceholder}
					/>
					{fieldError("budget")}
				</div>
			</div>

			<div className="space-y-2">
				<Label htmlFor={idOf("website")}>
					{form.website} {optionalMark}
				</Label>
				<Input
					{...fieldProps("website")}
					type="url"
					inputMode="url"
					autoComplete="url"
					placeholder={form.websitePlaceholder}
				/>
				{fieldError("website")}
			</div>

			<div
				aria-hidden="true"
				className="absolute -left-[9999px] h-px w-px overflow-hidden"
			>
				<label htmlFor={idOf("nickname")}>{form.honeypot}</label>
				<input
					id={idOf("nickname")}
					name="nickname"
					tabIndex={-1}
					autoComplete="off"
					value={values.nickname}
					onChange={update("nickname")}
				/>
			</div>

			{hasErrors ? (
				<p role="alert" className="text-sm font-medium text-destructive">
					{form.errors.summary}
				</p>
			) : null}

			{status === "failure" ? (
				<div
					ref={statusRef}
					tabIndex={-1}
					role="alert"
					className="flex gap-3 rounded-lg border border-destructive/40 bg-destructive/5 p-4 text-sm outline-none"
				>
					<CircleAlert
						aria-hidden="true"
						className="mt-0.5 size-4 shrink-0 text-destructive"
					/>
					<div className="space-y-1">
						<p className="font-medium">{form.failure.title}</p>
						<p className="text-muted-foreground">
							{form.failure.description}{" "}
							<a
								href={CONTACT_URL}
								className="font-medium text-foreground underline underline-offset-4"
							>
								{CONTACT_EMAIL}
							</a>
						</p>
					</div>
				</div>
			) : null}

			<div className="space-y-4">
				<Button type="submit" size="lg" disabled={status === "submitting"}>
					{status === "submitting" ? form.submitting : form.submit}
				</Button>
				<p className="text-sm text-muted-foreground">
					{form.privacy}{" "}
					<Link
						to="/{-$locale}/docs/$slug"
						params={{ ...localeParams, slug: "privacy" }}
						className="font-medium text-foreground underline underline-offset-4"
					>
						{form.privacyLink}
					</Link>
				</p>
			</div>
		</form>
	);
}
